import { FormEvent, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/features/auth/session-provider';
import { friendlyAuthError } from '@/features/auth/friendly-error';
import { InlineAlert } from '@/components/inline-alert';
import { CaremoryMark } from '@/components/icons/caremory-mark';

export function SignupPage() {
  const { session, loading } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  if (!loading && session) {
    return <Navigate to="/home" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    setSubmitting(true);
    const { data, error: authError } = await supabase.auth.signUp({
      email: trimmed,
      password,
    });
    setSubmitting(false);
    if (authError) {
      console.error('[signup] signUp failed', authError);
      setError(friendlyAuthError(authError));
      return;
    }
    if (!data.session) {
      setSubmittedEmail(trimmed);
    }
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 safe-top safe-bottom bg-gradient-to-b from-accent-soft/60 via-canvas to-canvas anim-fade-in">
      <div className="flex flex-col items-center mb-6">
        <CaremoryMark variant="primary" className="h-10 mb-3" title="Caremory" />
        <p className="text-sm font-semibold text-ink-muted text-center max-w-xs tracking-wide">
          Care that remembers
        </p>
      </div>

      {submittedEmail ? (
        <div className="card p-6 w-full max-w-sm text-center">
          <span
            aria-hidden="true"
            className="mx-auto mb-3 h-12 w-12 rounded-full bg-accent-soft text-accent flex items-center justify-center"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </span>
          <h2 className="text-lg font-bold mb-1">Check your email</h2>
          <p className="text-sm text-ink-muted mb-4">
            We sent a confirmation link to{' '}
            <span className="font-medium text-ink">{submittedEmail}</span>.
            Click the link, then sign in.
          </p>
          <Link to="/login" className="btn-primary w-full inline-flex">
            Back to sign in
          </Link>
          <p className="text-xs text-ink-subtle mt-4">
            Didn't get it? Check spam, or try signing in directly if email
            confirmation is disabled on your setup.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="card p-6 w-full max-w-sm">
          <h2 className="text-lg font-bold mb-1">Create account</h2>
          <p className="text-sm text-ink-muted mb-6">
            Start a health timeline for everyone you love.
          </p>

          <label className="label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input mb-4"
          />

          <label className="label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input mb-1"
          />
          <p className="text-xs text-ink-subtle mb-4">At least 6 characters.</p>

          {error && <InlineAlert className="mb-4">{error}</InlineAlert>}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full mb-3"
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>

          <p className="text-sm text-ink-muted text-center">
            Already have one?{' '}
            <Link to="/login" className="text-accent hover:text-accent-hover">
              Sign in
            </Link>
          </p>
        </form>
      )}
    </main>
  );
}
